import type { QueryClient } from '@tanstack/react-query';
import type { PlaylistEntry } from '@visious/core';
import { AUTH_QUERY_KEY } from '../api/auth';
import type { WatchLaterListResult } from './watchLaterModel';

/**
 * The Watch later membership cache, and the two writes that keep it honest (2026-08-03).
 *
 * Its own file rather than a section of `useWatchLater.ts`, and the reason is the bug this fixes.
 * The cache is *read* by every grid card's clock button, but the entry it holds can be added or
 * removed by four different controls — the clock itself, Save to playlist, the playlist page's
 * per-row remove, and the watch page's playlist panel — of which only the first went through the
 * hook that owned the patch. So removing a video from the appointed list on the playlist page left
 * every clock button on screen still saying "saved", and Save to playlist adding to that same list
 * left them all still offering to add it.
 *
 * Keeping the key and the two patches here lets `useEntryMutations` — the one path *all four* take —
 * own them, which is the only place that can be right for all four at once. `useOwnedPlaylists.ts`
 * holds the same shape of thing for the owned list and the playlist page's own read.
 *
 * Patching a key nothing has fetched is a no-op by construction: both updaters answer `previous`
 * when there is nothing there, and `setQueryData` does not create an entry for an `undefined`
 * result. So a mutation on a playlist that is *not* the appointed one costs nothing and needs no
 * test for which one is.
 */

const WATCH_LATER_KEY = 'watchLaterEntries';

/**
 * Keyed by grant as well as by playlist, 3a's rule: a *failure* is cached as a value here, so a
 * refusal caused by the previous token would otherwise be served to the new one.
 */
export function watchLaterQueryKey(playlistId: string, grantKey: number): readonly unknown[] {
  return [AUTH_QUERY_KEY, WATCH_LATER_KEY, playlistId, grantKey];
}

/**
 * Append, rather than invalidate: the list is the *complete* entry list of the appointed playlist
 * (`watchLaterModel.ts` says why complete is the only honest membership source), so refetching it
 * re-pages the whole thing to learn one row. The `201` already carries that row.
 */
export function noteWatchLaterEntryAdded(
  client: QueryClient,
  grantKey: number,
  playlistId: string,
  entry: PlaylistEntry,
): void {
  client.setQueryData<WatchLaterListResult>(watchLaterQueryKey(playlistId, grantKey), (previous) =>
    previous?.ok === true ? { ...previous, entries: [...previous.entries, entry] } : previous,
  );
}

/** The mirror image, by `indexId` — the address a removal is made with. */
export function noteWatchLaterEntryRemoved(
  client: QueryClient,
  grantKey: number,
  playlistId: string,
  indexId: string,
): void {
  client.setQueryData<WatchLaterListResult>(watchLaterQueryKey(playlistId, grantKey), (previous) =>
    previous?.ok === true
      ? { ...previous, entries: previous.entries.filter((entry) => entry.indexId !== indexId) }
      : previous,
  );
}
